/**
 * usePendingMcpConfig — the pre-session MCP tool-config buffer.
 *
 * The tool-config picker must be usable BEFORE the first message, so the choice
 * is held locally and folded into the create-session payload when the user's
 * first message creates the session. Once a session exists the picker writes
 * straight to it (`PATCH /sessions/{id}`) and this buffer must be empty —
 * a buffer that outlives the create would describe a session that no longer
 * needs describing.
 *
 * Owned here rather than in each embedder because two surfaces had grown their
 * own copy (`AIChatSidePanel` and the full `ChatPage`) and they had already
 * diverged. Mirrors `useChatSessionMachine`: one options object, one owner.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { McpConfig } from '../components/chat/types';
import { mcpConfigToContext } from '../components/chat/mcpSessionConfig';

export interface UsePendingMcpConfigOptions {
  /**
   * Drop the buffer whenever this changes. The embedder re-scoped the chat (the
   * global Luma panel does this on navigation), and a pre-session pick made for
   * the old scope is meaningless. Omit it where the surface has no embedder
   * scope of its own (the full chat page).
   */
  resetKey?: string;
}

export interface PendingMcpConfigBuffer {
  /** The buffered choice, or null when the picker has not been touched. */
  pendingConfig: McpConfig | null;
  /**
   * Ref mirror of `pendingConfig`, for the create path — which builds its
   * payload synchronously on send and would otherwise read a stale closure.
   */
  pendingConfigRef: React.MutableRefObject<McpConfig | null>;
  setPendingConfig: (config: McpConfig) => void;
  clearPendingConfig: () => void;
  /**
   * The buffered choice projected onto the snake_case session-context keys the
   * create endpoint reads. `{}` when nothing is buffered, so it spreads safely
   * over a base context.
   */
  toCreateContext: () => Record<string, unknown>;
}

export function usePendingMcpConfig(
  { resetKey }: UsePendingMcpConfigOptions = {},
): PendingMcpConfigBuffer {
  const [pendingConfig, setPendingConfigState] = useState<McpConfig | null>(null);
  const pendingConfigRef = useRef<McpConfig | null>(null);

  const setPendingConfig = useCallback((config: McpConfig) => {
    setPendingConfigState(config);
    pendingConfigRef.current = config;
  }, []);

  const clearPendingConfig = useCallback(() => {
    setPendingConfigState(null);
    pendingConfigRef.current = null;
  }, []);

  const toCreateContext = useCallback(
    () => mcpConfigToContext(pendingConfigRef.current),
    [],
  );

  useEffect(() => {
    if (resetKey === undefined) return;
    clearPendingConfig();
  }, [resetKey, clearPendingConfig]);

  return {
    pendingConfig,
    pendingConfigRef,
    setPendingConfig,
    clearPendingConfig,
    toCreateContext,
  };
}
